/* eslint-disable */
import React, { useState, useEffect, useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { Formik, Form, Field } from 'formik';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import * as yup from 'yup';
import apollo from '../../services/apollo';
import { DropdownWithValidation } from '../../components/prime-formik';
import { NotificationContext } from '../../components/GrowlNotifications';
import { growlActionSuccess, growlErrorsWithMessage } from '../../utils/NotificationUtil';

const messages = defineMessages({
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  futureOwner: {
    id: 'users.future_owner',
    defaultMessage: 'Future owner',
  },
  required: {
    id: 'errors.validation.required',
    defaultMessage: 'Required',
  },
  success: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  error: {
    id: 'alert.error.generic',
    defaultMessage: 'We have encountered a problem. Please try again later.',
  },
  header: {
    id: 'user.ownership_change',
    defaultMessage: 'Change ownership of account',
  },
});

const USER_LIST_QUERY = gql`
  query($accountId: ID!, $after: String, $before: String, $first: Int, $last: Int) {
    account(id: $accountId) {
      id
      users(after: $after, before: $before, first: $first, last: $last) {
        indexFrom
        indexTo
        totalCount
        pageInfo {
          endCursor
          startCursor
          hasNextPage
          hasPreviousPage
        }
        edges {
          role
          node {
            id
            email
            firstName
            lastName
            enabled
            createdOn
          }
        }
      }
    }
  }
`;

const CHANGE_ACCOUNT_OWNER = gql`
  mutation($input: ChangeAccountOwnershipInput!) {
    giveOwnershipOfAccountToUser(input: $input) {
      id
    }
  }
`;

const ChangeAccountOwner = ({ accountId, toggleChangeOwner }) => {
  const [users, setUsers] = useState([]);

  const { formatMessage } = useIntl();
  const growl = useContext(NotificationContext);

  const getUsers = async () => {
    const result = await apollo.query({
      query: USER_LIST_QUERY,
      variables: {
        accountId,
      },
    });

    if (result && result.data && result.data.account && result.data.account.users) {
      const { edges } = result.data.account.users;

      const userResult = edges.filter((edge) => !edge.role.includes('owner')).map((edge) => edge.node);

      setUsers(userResult);
    }
  };

  const changeOwner = async (values) => {
    const { userId } = values;

    try {
      await apollo.mutate({
        mutation: CHANGE_ACCOUNT_OWNER,
        variables: {
          input: {
            accountId,
            newOwnerId: userId,
          },
        },
        refetchQueries: ['getUserById', 'getViewerAccounts'],
      });
      growlActionSuccess(growl, formatMessage(messages.success));
      toggleChangeOwner(false);
    } catch (error) {
      growlErrorsWithMessage(growl, formatMessage(messages.error));
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  const futureOwnerOptions =
    users.length > 0
      ? users.map((user) => ({
          label: `${user.firstName} ${user.lastName}`,
          value: user.id,
        }))
      : [];

  const initialValues = {
    userId: futureOwnerOptions.length > 0 ? futureOwnerOptions[0].value : null,
  };

  const validationSchema = yup.object({
    userId: yup
      .string()
      .nullable()
      .required(formatMessage(messages.required)),
  });

  return (
    <Panel header={formatMessage(messages.header)}>
      <div className="p-grid p-fluid">
        <div className="p-col-2">
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { setSubmitting }) => {
              changeOwner(values);
              setSubmitting(false);
            }}
          >
            {({ isSubmitting }) => (
              <Form>
                <input type="hidden" name="account" value={accountId} />
                <div className="p-grid">
                  <div className="p-col-12">
                    <Field
                      id="userId"
                      name="userId"
                      labelText={formatMessage(messages.futureOwner)}
                      component={DropdownWithValidation}
                      options={futureOwnerOptions}
                      showClear
                    />
                  </div>
                </div>
                <div className="p-grid">
                  <div className="p-col-6">
                    <Button
                      type="button"
                      className="p-button-secondary"
                      label={formatMessage(messages.cancel)}
                      onClick={() => toggleChangeOwner(false)}
                    />
                  </div>
                  <div className="p-col-6">
                    <Button type="submit" label={formatMessage(messages.submit)} disabled={isSubmitting} />
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </Panel>
  );
};

ChangeAccountOwner.propTypes = {
  accountId: PropTypes.string.isRequired,
  toggleChangeOwner: PropTypes.func.isRequired,
};

export default ChangeAccountOwner;
